import React, {
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import qs from "qs";
import { getQuery } from "@/service/http";
import { EmptyIcon } from "../../icons";
import { Tooltip } from "antd";
import Loading from "../../components/dc-loading";
import { useTranslation } from "react-i18next";
import { useRequest } from "ahooks";
import { useStores } from "@/store";
import { useFilterContext } from "../head-bar/filter-context";

import "./index.less";

const noop = () => {};

const defaultKeywords = [];

const normalizeKeyword = (item, index) => {
  if (typeof item === "string") return { id: `${index}`, label: item };
  if (!item) return { id: `${index}`, label: "" };
  const id = item.id ?? item.value ?? `${index}`;
  const label =
    item.label ?? item.name ?? item.title ?? String(item.value ?? "");
  return { id: String(id), label };
};

const findMatch = (label, query) => {
  if (!query) return null;
  const lowerLabel = label.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const idx = lowerLabel.indexOf(lowerQuery);
  if (idx < 0) return null;
  return { start: idx, end: idx + query.length };
};

const renderHighlightedLabel = (label, query) => {
  const match = findMatch(label, query);
  if (!match) return label;
  const before = label.slice(0, match.start);
  const hit = label.slice(match.start, match.end);
  const after = label.slice(match.end);
  return (
    <>
      {before}
      <span className="ui-keyword-picker__match">{hit}</span>
      {after}
    </>
  );
};

/**
 * KeywordPicker (Explore redesign)
 *
 * Search suggestions dropdown.
 */
const KeywordPicker = ({
  query = "",
  ariaLabel = "Search suggestions",
  className = "",
  style,
  onSelect = noop,
  // Optional controlled mode (legacy): if these are provided, KeywordPicker won't fetch internally.
  keywords,
  loading,
  showMore,
  onShowMore,
}) => {
  let history = useHistory();
  const { t } = useTranslation();

  const { CommonStore } = useStores();
  const { searchState: filterState, state, lang } = useFilterContext();

  const isControlled =
    keywords !== undefined ||
    loading !== undefined ||
    showMore !== undefined ||
    onShowMore !== undefined;

  const [internalKeywords, setInternalKeywords] = useState(defaultKeywords);
  const [internalNextPage, setInternalNextPage] = useState(1);
  const [internalTotal, setInternalTotal] = useState(null);
  const [isSearchTyping, setIsSearchTyping] = useState(false);
  const [isAppending, setIsAppending] = useState(false);
  const searchCacheRef = useRef(new Map());
  const searchInFlightRef = useRef(new Map());
  const lastSearchTrimRef = useRef("");

  const scrollRootRef = useRef(null);
  const sentinelRef = useRef(null);
  const loadLockRef = useRef(false);
  const lastListLenRef = useRef(0);

  const searchTrim = useMemo(() => (query || "").trim(), [query]);
  const region = filterState?.region;
  const lat = state?.pos?.lat;
  const lng = state?.pos?.lng;

  const normalizeHeaderNumber = useCallback((val) => {
    if (val === undefined || val === null) return null;
    const n = Number(val);
    return Number.isFinite(n) ? n : null;
  }, []);

  const extractTotalFromHeaders = useCallback(
    (headers) => {
      if (!headers) return null;
      return normalizeHeaderNumber(headers.total_count);
    },
    [normalizeHeaderNumber]
  );

  const makeSearchCacheKey = useCallback(({ q, region, lang }) => {
    return `${lang || ""}::${region || ""}::${(q || "").trim()}`;
  }, []);

  const hydrateFromCache = useCallback(
    ({ q, region, lang }) => {
      const key = makeSearchCacheKey({ q, region, lang });
      const cached = searchCacheRef.current.get(key);
      if (!cached) return false;
      if (!Array.isArray(cached.data)) return false;
      if (typeof cached.total !== "number") return false;
      setInternalKeywords(Array.isArray(cached.data) ? cached.data : []);
      setInternalNextPage(
        cached.nextPage === null || cached.nextPage === undefined
          ? null
          : Number.isFinite(Number(cached.nextPage))
          ? Number(cached.nextPage)
          : 1
      );
      setInternalTotal(
        Number.isFinite(Number(cached.total)) ? Number(cached.total) : null
      );
      return true;
    },
    [makeSearchCacheKey]
  );

  const runSearch = useCallback(
    async ({ page = 1, append = false } = {}) => {
      const q = searchTrim;
      const key = makeSearchCacheKey({ q, region, lang });

      if (!q || !region) {
        setInternalKeywords([]);
        setInternalNextPage(1);
        setInternalTotal(null);
        return null;
      }

      const inflightKey = `${key}::${page}::${append ? "append" : "replace"}`;
      if (searchInFlightRef.current.has(inflightKey)) {
        return searchInFlightRef.current.get(inflightKey);
      }

      const p = CommonStore.getBrowseRestaurants(
        {
          api_key: "cgecegcegcc",
          lang,
          q,
          lat,
          lng,
          per_page: 10,
          page,
        },
        region
      )
        .then((res) => {
          const data = Array.isArray(res?.data) ? res.data : [];
          const headers = res?.headers || {};
          const nextPage = normalizeHeaderNumber(headers.next_page);
          const total = extractTotalFromHeaders(headers);

          const effectiveNextPage =
            nextPage !== null && nextPage > page ? nextPage : null;

          setInternalNextPage(effectiveNextPage);
          setInternalTotal(total);
          setInternalKeywords((prev) => (append ? [...prev, ...data] : data));

          const nextData = append
            ? [...(searchCacheRef.current.get(key)?.data || []), ...data]
            : data;

          if (typeof total === "number") {
            searchCacheRef.current.set(key, {
              data: nextData,
              nextPage: effectiveNextPage,
              total,
            });
          }
        })
        .finally(() => {
          searchInFlightRef.current.delete(inflightKey);
        });

      searchInFlightRef.current.set(inflightKey, p);
      return p;
    },
    [
      CommonStore,
      extractTotalFromHeaders,
      lang,
      lat,
      lng,
      makeSearchCacheKey,
      normalizeHeaderNumber,
      region,
      searchTrim,
    ]
  );

  useEffect(() => {
    if (isControlled) return;
    const nextTrim = searchTrim;
    const prevTrim = lastSearchTrimRef.current;
    lastSearchTrimRef.current = nextTrim;

    if (nextTrim !== prevTrim) {
      setInternalKeywords([]);
      setInternalNextPage(1);
      setInternalTotal(null);
      if (nextTrim) setIsSearchTyping(true);
    }

    if (!nextTrim || !region) {
      setInternalKeywords([]);
      setInternalNextPage(1);
      setInternalTotal(null);
      setIsSearchTyping(false);
    }
  }, [isControlled, searchTrim, region]);

  const { loading: searchLoading } = useRequest(
    async () => {
      if (isControlled) return null;

      const q = searchTrim;

      if (!q || !region) {
        setIsSearchTyping(false);
        return null;
      }

      if (hydrateFromCache({ q, region, lang })) {
        setIsSearchTyping(false);
        return null;
      }

      try {
        await runSearch({ page: 1, append: false });
      } finally {
        setIsSearchTyping(false);
      }

      return null;
    },
    {
      refreshDeps: [searchTrim, region, lang],
      debounceWait: 300,
    }
  );

  const internalShouldShowMore = useMemo(() => {
    if (isControlled) return Boolean(showMore);
    if (!searchTrim) return false;
    if (!(typeof internalTotal === "number" && internalTotal > 10)) return false;
    if (!Array.isArray(internalKeywords)) return false;
    if (internalNextPage === null) return false;
    return internalKeywords.length < internalTotal;
  }, [
    internalKeywords,
    internalNextPage,
    internalTotal,
    isControlled,
    searchTrim,
    showMore,
  ]);

  const handleShowMore = useCallback(async () => {
    if (isControlled) {
      if (typeof onShowMore === "function") onShowMore();
      return;
    }
    if (!internalShouldShowMore) return;
    if (internalNextPage === null) return;
    if (isAppending) return;

    setIsAppending(true);
    try {
      await runSearch({ page: internalNextPage, append: true });
    } finally {
      setIsAppending(false);
    }
  }, [
    internalNextPage,
    internalShouldShowMore,
    isAppending,
    isControlled,
    onShowMore,
    runSearch,
  ]);

  const goRestaurant = (item) => {
    const {
      lang,
      region,
      private_token,
      callback,
      payment,
      login_url,
      source,
      gid,
    } = getQuery();
    let obj = {
      gid,
      lang,
      region,
      private_token,
      callback,
      payment,
      login_url,
      source,
    };
    Object.keys(obj).forEach((key) => {
      if (obj[key] === "" || obj[key] == null) {
        obj[key] = null;
      }
    });
    let queryString = qs.stringify(obj, {
      skipNulls: true,
    });

    history.push(`/restaurants/${item.id}?${queryString}`);
    window.top.postMessage(
      {
        type: "other",
        params: {
          url: item.website_detail_url,
          region,
          lang,
        },
      },
      "*"
    );
    onSelect();
  };
  const effectiveKeywords = isControlled
    ? keywords || defaultKeywords
    : internalKeywords;

  const isInitialLoading = isControlled
    ? Boolean(loading)
    : Boolean(searchLoading || isSearchTyping);

  const list = useMemo(() => {
    const normalized = (effectiveKeywords || [])
      .map(normalizeKeyword)
      .filter((i) => i.label);
    return normalized;
  }, [effectiveKeywords]);

  useEffect(() => {
    if (lastListLenRef.current !== list.length) {
      lastListLenRef.current = list.length;
      loadLockRef.current = false;
    }
  }, [list.length]);

  const canAutoLoadMore =
    internalShouldShowMore && !isInitialLoading && !isAppending;

  useEffect(() => {
    if (!canAutoLoadMore) return;

    const rootEl = scrollRootRef.current;
    const sentinelEl = sentinelRef.current;
    if (!rootEl || !sentinelEl) return;

    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries && entries[0];
        if (!entry || !entry.isIntersecting) return;
        if (loadLockRef.current) return;
        loadLockRef.current = true;
        handleShowMore();
      },
      {
        root: rootEl,
        threshold: 0.1,
        rootMargin: "0px 0px 40px 0px",
      }
    );

    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [canAutoLoadMore, handleShowMore]);

  const handleScroll = useCallback(
    (e) => {
      if (!canAutoLoadMore) return;
      if (typeof IntersectionObserver !== "undefined") return;

      const el = e.currentTarget;
      if (!el) return;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
      if (!nearBottom) return;
      if (loadLockRef.current) return;
      loadLockRef.current = true;
      handleShowMore();
    },
    [canAutoLoadMore, handleShowMore]
  );

  return (
    <div
      className={`ui-keyword-picker ${className}`.trim()}
      style={style}
      role="listbox"
      aria-label={ariaLabel}
      ref={scrollRootRef}
      onScroll={handleScroll}
    >
      {isInitialLoading ? (
        <div className="ui-head-bar__keywordLoading" role="status">
          <Loading red={true} />
        </div>
      ) : list.length ? (
        list.map((item) => (
          <div
            key={item.id}
            className="ui-keyword-picker__item"
            onClick={(e) => {
              e.preventDefault();
              goRestaurant(item);
            }}
            role="option"
            aria-selected={false}
            aria-label={item.label}
          >
            <Tooltip title={item.label} placement="topLeft">
              <span className="ui-keyword-picker__label">
                {renderHighlightedLabel(item.label, query)}
              </span>
            </Tooltip>
          </div>
        ))
      ) : (
        <div className="ui-head-bar__keywordEmpty" role="status">
          <EmptyIcon />
          <p> {t("Header.No results found")} </p>
        </div>
      )}

      {isAppending && (
        <div
          className="ui-head-bar__keywordLoading ui-head-bar__keywordLoading--inline"
          role="status"
        >
          <Loading red={true} />
        </div>
      )}

      {!isInitialLoading && internalShouldShowMore && (
        <div
          ref={sentinelRef}
          className="ui-keyword-picker__sentinel"
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default KeywordPicker;
